import {Box, Heading, HStack, Icon, Text, Textarea} from "@chakra-ui/react";
import {Button, ButtonProps} from "../../../components/ui/button";
import {PopoverContent, PopoverRoot, PopoverTrigger} from "../../../components/ui/popover";
import {SegmentedControl} from "../../../components/ui/segmented-control";
import {ReviewAction, ReviewStatus} from "platformed-client/types";
import {REVIEW_STATUS_MAP} from "../ReviewStatus";
import {memo, useState} from "react";
import {ReviewItem} from ".";
import {usePromiseState} from "../../../hooks/promiseState";
import {useCreateReviews} from "./useCreateReviews";
import T from "platformed-components/i18n/T";
import useTranslate from "platformed-components/i18n/useTranslate";

const SubmitReviewButton = memo(
  ({reviewItem, review, ...props}: {reviewItem: ReviewItem; review: ReviewAction} & ButtonProps) => {
    const t = useTranslate();
    const createReviews = useCreateReviews(reviewItem);
    const reviewStatus = review.review_status;

    const [reviewType, setReviewType] = useState<ReviewStatus>(reviewStatus === "Pending" ? "Commented" : reviewStatus);
    const [open, setOpen] = useState(false);

    const [comment, setComment] = useState(review.comment ? review.comment : "");

    const [creatingReview, createReview] = usePromiseState(async () => {
      await createReviews([
        {
          review_status: reviewType,
          comment: comment === "" ? undefined : comment,
          reviewer_id: review.reviewer.owner_id,
          reviewer_role_id: review.reviewer_role_id,
        },
      ]);

      setOpen(false);
    }, [createReviews, review.reviewer.owner_id, review.reviewer_role_id, reviewType, comment]);

    return (
      <PopoverRoot open={open} onOpenChange={e => setOpen(e.open)}>
        <PopoverTrigger asChild>
          <Button {...props} />
        </PopoverTrigger>
        <PopoverContent p={0}>
          <Box bg="gray.50" p={3} borderBottom="1px solid {colors.gray.200}">
            <Heading size="sm">
              <T keyName="c.Finish_your_review" />
            </Heading>
            <Box pt={3}>
              <SegmentedControl
                onValueChange={e => setReviewType(e.value as ReviewStatus)}
                defaultValue={reviewType}
                items={["Commented", "Approved", "RequestedChanges"].map(status => ({
                  value: status,
                  label: (
                    <HStack>
                      <Icon asChild boxSize="1em" color={`${REVIEW_STATUS_MAP[status].color}.600`}>
                        {REVIEW_STATUS_MAP[status].icon}
                      </Icon>
                      <Text>{REVIEW_STATUS_MAP[status].actionText}</Text>
                    </HStack>
                  ),
                }))}
              />
            </Box>
          </Box>
          <Box p={3}>
            <Textarea
              minH="8rem"
              placeholder={t("c.Leave_a_comment")}
              value={comment}
              onChange={e => setComment(e.target.value)}
            />
          </Box>
          <Box bg="gray.50" p={3} borderTop="1px solid {colors.gray.200}">
            <Button
              w="full"
              size="sm"
              colorPalette="green"
              disabled={
                creatingReview.inProgress ||
                ((reviewType === "Commented" || reviewType === "RequestedChanges") && !comment)
              }
              onClick={createReview}
            >
              <T keyName="c.Submit_review" />
            </Button>
          </Box>
        </PopoverContent>
      </PopoverRoot>
    );
  },
);

export default SubmitReviewButton;
